/**
 * Contact / Collaboration Page
 * Interest-based contact form with category selection
 */

import React, { useState } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const categories = [
    {
      value: 'brand',
      label: 'Brand Collaboration',
      icon: '🤝',
      description: 'Partner with me for brand promotions and sponsorships',
    },
    {
      value: 'speaking',
      label: 'Speaking Engagement',
      icon: '🎤',
      description: 'Invite me to speak at your event or conference',
    },
    {
      value: 'consulting',
      label: 'Consulting',
      icon: '💡',
      description: 'Get advice on productivity, fitness, or content creation',
    },
    {
      value: 'media',
      label: 'Media Inquiry',
      icon: '📰',
      description: 'Press, interviews, and media opportunities',
    },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // In production, send to backend API or email service
    // For now, simulate submission
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setSubmitted(true);
      setLoading(false);
    }, 1000);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-6xl mb-4">✓</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Thank You!
            </h2>
            <p className="text-gray-600 mb-8">
              Your message has been received. I'll get back to you as soon as possible.
            </p>
            <a
              href="/"
              className="inline-block bg-gray-900 text-white px-8 py-3 rounded-full hover:bg-gray-800 transition-colors"
            >
              Back to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <a
            href="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </a>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Let's Collaborate
          </h1>
          <p className="text-xl text-gray-600">
            I'd love to hear from you. Choose a category and let's connect.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Category Selection */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-4">
                What brings you here?
              </label>
              <div className="grid md:grid-cols-2 gap-4">
                {categories.map((cat) => (
                  <label
                    key={cat.value}
                    className={`
                      relative flex items-start p-4 border-2 rounded-xl cursor-pointer
                      transition-all
                      ${formData.category === cat.value
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="category"
                      value={cat.value}
                      checked={formData.category === cat.value}
                      onChange={handleChange}
                      className="sr-only"
                      required
                    />
                    <span className="text-3xl mr-3">{cat.icon}</span>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 mb-1">
                        {cat.label}
                      </div>
                      <div className="text-sm text-gray-600">
                        {cat.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-lg font-semibold text-gray-900 mb-2">
                Your Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-600 focus:outline-none transition-colors"
                placeholder="Jane Doe"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-lg font-semibold text-gray-900 mb-2">
                Your Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-600 focus:outline-none transition-colors"
                placeholder="jane@example.com"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-lg font-semibold text-gray-900 mb-2">
                Your Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-600 focus:outline-none transition-colors resize-none"
                placeholder="Tell me more about your inquiry..."
              />
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-900 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
